import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import anime from 'animejs/lib/anime.es.js';
import { imgIxParams } from 'app/constants/img-ix-params';

import { AppStateService } from 'app/services/app-state.service';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { PrimaryBtnComponent } from 'app/components/ui-kit/buttons/primary-btn/primary-btn.component';

import { AgentDataCardInlineComponent } from '../agent-data-card-inline/agent-data-card-inline.component';
import { SecondaryBtnComponent } from 'app/components/ui-kit/buttons/secondary-btn/secondary-btn.component';
import { AiTypingComponent } from "../../../../../components/distinct-components/ai-visual/ai-typing/ai-typing.component";
import { AgentSetupWatcherComponent } from '../agent-setup-watcher/agent-setup-watcher.component';
@Component({
  selector: 'app-agent-conversation-minimal',
  standalone: true,
  imports: [CommonModule, SvgLoaderComponent, AgentDataCardInlineComponent, PrimaryBtnComponent, SecondaryBtnComponent, AiTypingComponent, AgentSetupWatcherComponent],
  templateUrl: './agent-conversation-minimal.component.html',
  styleUrls: ['./agent-conversation-minimal.component.scss'],
})
export class AgentConversationMinimalComponent implements OnInit, OnDestroy {
  currentConversation: any[] = [];
  currentDataModel: any[] = [];
  userProfile: any;
  showAssignToAgent: boolean = false;
  agentTypedText = '';
  private _typingInterval?: number;

  @ViewChild('exchangeWrapper', { static: false }) exchangeElRef: ElementRef<HTMLElement>;

  optimizedProfileImgURL: string = null;

  private initialViewportHeight = 0;
  private keyboardCheckInterval: any;
  private debouncedViewportResize = this.debounce(
    this.handleViewportResize.bind(this),
    200
  );

  isKeyboardActive: boolean = false;
  isKeyboardDoneShowing: boolean = false;

  hasMatches: boolean = false;

  constructor(public appState: AppStateService, private el: ElementRef) {
    effect(() => {
      const convo = this.appState.agent.currentConversation();
      if (convo && convo.length) {
        this.currentConversation = convo.slice(-1);
        this.handleScrollToLastMessage();
        this.typeOutText(this.currentConversation[0].agent || '');
      }
    });
    effect(() => {
      const dataModel = this.appState.agent.currentDataModel();
      if (dataModel) {
        this.currentDataModel = this.appState.agent.currentDataModel();
        this.handleScrollToLastMessage();
      }
    });
    effect(() => {
      const profile = this.appState.user.profile();
      this.userProfile = profile;
      if (profile?.profileImgURL) {
        this.optimizedProfileImgURL = profile.profileImgURL + imgIxParams.profile.xs;
      }
    });
    effect(() => {
      const matches = this.appState.agent.refinedFlightOptions();
      if (matches.optimalMatches?.length > 0 || matches.similarMatches?.length > 0) {
        this.hasMatches = true;
      } else {
        this.hasMatches = false;
      }
    });
  }

  ngOnInit(): void {
    if (window.visualViewport) {
      this.initialViewportHeight = window.visualViewport.height;
      window.visualViewport.addEventListener('resize', this.debouncedViewportResize);
    } else {
      this.initialViewportHeight = window.innerHeight;
      this.keyboardCheckInterval = setInterval(() => {
        const heightDiff = this.initialViewportHeight - window.innerHeight;
        const keyboardIsNowVisible = heightDiff > 150;

        if (keyboardIsNowVisible !== this.isKeyboardActive) {
          this.isKeyboardActive = keyboardIsNowVisible;
          this.isKeyboardActive ? this.onKeyboardShow() : this.onKeyboardHide();
        }
      }, 300);
    }
  }

  ngAfterViewInit() {
    this.handleScrollToLastMessage();
  }

  private handleViewportResize(): void {
    const heightDiff = this.initialViewportHeight - window.visualViewport.height;
    const keyboardIsNowVisible = heightDiff > 150;
    if (keyboardIsNowVisible !== this.isKeyboardActive) {
      this.isKeyboardActive = keyboardIsNowVisible;
      this.isKeyboardActive ? this.onKeyboardShow() : this.onKeyboardHide();
    }
  }

  private onKeyboardShow(): void {
    this.isKeyboardActive = true;
    this.isKeyboardDoneShowing = true;
    this.adjustExchangeWrapperHeight();
  }

  private onKeyboardHide(): void {
    this.isKeyboardActive = false;
    anime({
      targets: '#ex-wrap',
      translateY: [6, 0],
      duration: 1000,
      easing: 'easeOutSine',
    });
    this.resetExchangeWrapperHeight();
  }

  private adjustExchangeWrapperHeight(): void {
    const exchangeEl = this.exchangeElRef?.nativeElement;
    if (window.visualViewport && exchangeEl) {
      const viewportHeight = window.visualViewport.height;
      const inputWrapperHeight = 97;
      const adjustedHeight = viewportHeight - inputWrapperHeight;

      exchangeEl.style.height = `${adjustedHeight}px`;
      this.handleScrollToLastMessage();

    }
  }

  private resetExchangeWrapperHeight(): void {
    const exchangeEl = this.exchangeElRef?.nativeElement;
    this.isKeyboardDoneShowing = false;
    if (exchangeEl) {
      exchangeEl.style.height = "";
      this.handleScrollToLastMessage();
    }
  }

  private handleScrollToLastMessage() {
    const el = this.exchangeElRef?.nativeElement;
    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: 'auto'
      });
    });
  }

  onClickViewOptions(): void {
    this.appState.agent.mode.set('results');
    const backButton = {
      action: 'route',
      value: '/agent?m=agent'
    }
    this.appState.navigation.backButtonState.set(backButton);
  }

  onClickSetupAgent() {
    this.showAssignToAgent = true;
  }

  onClickCancelAssign() {
    this.showAssignToAgent = false;
  }

  private debounce<T extends (...args: any[]) => void>(func: T, wait: number): T {
    let timeout: any;
    return function (...args: any[]) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    } as T;
  }

  typeOutText(fullText: string, speed: number = 20): void {
    if (this._typingInterval != null) {
      clearInterval(this._typingInterval);
      this._typingInterval = undefined;
    }
    this.agentTypedText = '';

    setTimeout(() => {
      let index = 0;

      this._typingInterval = window.setInterval(() => {
        this.agentTypedText += fullText.charAt(index);
        index++;

        if (index >= fullText.length) {
          if (this._typingInterval != null) {
            clearInterval(this._typingInterval);
            this._typingInterval = undefined;
          }
        }
      }, speed);
    }, 0);
  }

  onClickMakeChanges() {
    this.appState.agent.isNoResult.set(false);
    this.appState.agent.refinedFlightOptions.set({})
    this.appState.agent.hideInput.set(false);
  }

  ngOnDestroy(): void {
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.debouncedViewportResize);
    }
    if (this.keyboardCheckInterval) {
      clearInterval(this.keyboardCheckInterval);
    }
  }

}
